import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  IconCar, IconUsers, IconCalendarEvent, IconShieldCheck,
  IconBrandInstagram, IconBrandFacebook, IconPhone, IconMail,
  IconMapPin, IconArrowRight, IconCheck, IconSparkles,
  IconTrophy, IconClock, IconStarFilled
} from '@tabler/icons-react';

// Shiny Vegas Gold gradient animation - from GarageHeaderProfessional design system
const shinyGoldStyles = `
  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  @keyframes glow-pulse {
    0%, 100% { box-shadow: 0 0 20px rgba(174,163,109,0.3), 0 0 40px rgba(174,163,109,0.1); }
    50% { box-shadow: 0 0 40px rgba(174,163,109,0.6), 0 0 80px rgba(174,163,109,0.3); }
  }

  .text-gradient-gold {
    background: linear-gradient(135deg, #aea36d 0%, #c4b877 50%, #aea36d 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .animate-gradient-shift {
    animation: gradient-shift 3s ease infinite;
  }

  .animate-shimmer {
    animation: shimmer 3s linear infinite;
  }

  .glow-gold {
    animation: glow-pulse 2s ease-in-out infinite;
  }

  .border-glow-gold {
    box-shadow: 0 0 20px rgba(174,163,109,0.3);
  }

  .border-glow-gold:hover {
    box-shadow: 0 0 30px rgba(174,163,109,0.5), 0 0 60px rgba(174,163,109,0.3);
  }
`;

// Inject shiny gold styles
if (typeof document !== 'undefined' && !document.getElementById('shiny-gold-styles')) {
  const style = document.createElement('style');
  style.id = 'shiny-gold-styles';
  style.innerHTML = shinyGoldStyles;
  document.head.appendChild(style);
}

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return <span>{count.toLocaleString()}{suffix}</span>;
};

// Hero Section with Parallax
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black pt-20">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #aea36d 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-vegas-gold rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 h-full flex items-center justify-center px-6"
      >
        <div className="max-w-6xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vegas-gold/10 border border-vegas-gold/30 rounded-full mb-8"
          >
            <IconSparkles className="w-5 h-5 text-vegas-gold" />
            <span className="text-vegas-gold font-medium">Serving Since 2009</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-6xl md:text-8xl font-bold mb-6 text-white leading-tight"
          >
            Hospitality Driven
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-vegas-gold via-yellow-400 to-vegas-gold">
              Valet Excellence
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto"
          >
            Transforming ordinary parking into extraordinary experiences with
            professionalism, precision, and cutting-edge technology.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group relative px-8 py-4 bg-gradient-to-r from-vegas-gold to-yellow-600 rounded-xl font-bold text-black text-lg overflow-hidden shadow-2xl"
            >
              <span className="relative z-10 flex items-center gap-2">
                Get Instant Quote
                <IconArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-vegas-gold opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-xl font-bold text-white text-lg hover:bg-white/20 transition-all"
            >
              Explore Services
            </motion.button>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { value: 15, suffix: '+', label: 'Years Experience' },
              { value: 500, suffix: '+', label: 'Events Completed' },
              { value: 160, suffix: '+', label: 'Professional Valets' },
              { value: 98, suffix: '%', label: 'Client Satisfaction' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-vegas-gold mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-vegas-gold rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-vegas-gold rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

// Services Grid with Hover Effects
const ServicesSection = () => {
  const services = [
    {
      icon: IconCar,
      title: 'Commercial Valet',
      description: 'Premium valet services for hotels, restaurants, and corporate venues',
      features: ['24/7 Availability', 'Professional Staff', 'Custom Branding'],
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: IconCalendarEvent,
      title: 'Event Services',
      description: 'Seamless parking management for weddings, galas, and special events',
      features: ['Event Planning', 'Peak Hour Support', 'Guest Experience'],
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      icon: IconUsers,
      title: 'Shuttle Services',
      description: 'Efficient transportation solutions for large gatherings and venues',
      features: ['Route Optimization', 'Luxury Vehicles', 'Real-time Tracking'],
      gradient: 'from-green-500 to-green-600'
    }
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-950">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-white mb-4">
            Our <span className="text-vegas-gold">Services</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Comprehensive valet solutions tailored to your unique needs
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-vegas-gold transition-all duration-300 overflow-hidden"
            >
              {/* Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />

              {/* Icon */}
              <div className={`inline-flex p-4 bg-gradient-to-br ${service.gradient} rounded-xl mb-6 relative z-10`}>
                <service.icon className="w-8 h-8 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-3">{service.title}</h3>
              <p className="text-gray-400 mb-6">{service.description}</p>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300">
                    <IconCheck className="w-5 h-5 text-vegas-gold flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button className="w-full py-3 bg-vegas-gold/10 border border-vegas-gold/30 rounded-lg text-vegas-gold font-semibold hover:bg-vegas-gold hover:text-black transition-all group-hover:scale-105">
                Learn More
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// GSEAT Platform Showcase
const PlatformSection = () => {
  return (
    <section className="py-24 px-6 bg-gray-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-vegas-gold/10 border border-vegas-gold/30 rounded-full mb-6">
              <IconTrophy className="w-5 h-5 text-vegas-gold" />
              <span className="text-vegas-gold font-medium">Powered by GSEAT</span>
            </div>

            <h2 className="text-5xl font-bold text-white mb-6">
              Technology-Driven
              <br />
              <span className="text-vegas-gold">Excellence</span>
            </h2>

            <p className="text-xl text-gray-400 mb-8">
              Our revolutionary GSEAT platform connects top-tier valet professionals
              with opportunities, ensuring you always get the best service.
            </p>

            {/* Features */}
            <div className="space-y-4">
              {[
                { icon: IconShieldCheck, title: 'Verified Professionals', desc: 'Background-checked and certified valets' },
                { icon: IconClock, title: 'Real-Time Tracking', desc: 'Monitor your vehicle in real-time' },
                { icon: IconSparkles, title: 'Seamless Experience', desc: 'From drop-off to pick-up, effortless service' }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4 items-start bg-gray-900/50 p-4 rounded-xl border border-gray-800 hover:border-vegas-gold/30 transition-colors"
                >
                  <div className="p-3 bg-vegas-gold/10 rounded-lg">
                    <item.icon className="w-6 h-6 text-vegas-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <button className="mt-8 px-8 py-4 bg-gradient-to-r from-vegas-gold to-yellow-600 rounded-xl font-bold text-black hover:scale-105 transition-transform">
              Discover GSEAT Platform
            </button>
          </motion.div>

          {/* Right - Platform Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 shadow-2xl">
              {/* Mock App Interface */}
              <div className="space-y-4">
                <div className="h-12 bg-vegas-gold/20 rounded-lg animate-pulse" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-32 bg-gray-700/50 rounded-lg" />
                  <div className="h-32 bg-gray-700/50 rounded-lg" />
                </div>
                <div className="h-24 bg-gray-700/30 rounded-lg" />
                <div className="space-y-2">
                  <div className="h-8 bg-gray-700/40 rounded" />
                  <div className="h-8 bg-gray-700/40 rounded w-3/4" />
                </div>
              </div>

              {/* Floating Badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
                className="absolute -top-6 -right-6 bg-green-500 text-white px-6 py-3 rounded-full font-bold shadow-lg"
              >
                Live Now
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Carousel
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Hotel Manager',
      company: 'Luxury Resort NJ',
      text: 'Always a pleasure! I feel like I am leaving my car in very capable hands. The professionalism is unmatched.',
      rating: 5,
      image: '👩‍💼'
    },
    {
      name: 'Michael Chen',
      role: 'Event Coordinator',
      company: 'Premier Events PA',
      text: 'So quick & easy -- I love this! ODV made our 500-person gala seamless. Absolutely exceptional service.',
      rating: 5,
      image: '👨‍💼'
    },
    {
      name: 'Jennifer Martinez',
      role: 'Restaurant Owner',
      company: 'Fine Dining Philly',
      text: 'Their valet team treats every guest like VIP. Our customer satisfaction scores increased by 30% since partnering with ODV.',
      rating: 5,
      image: '👩‍🍳'
    }
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-gray-950 to-black">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold text-white mb-4">
            Trusted by <span className="text-vegas-gold">Industry Leaders</span>
          </h2>
          <p className="text-xl text-gray-400">Don't just take our word for it</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-vegas-gold transition-all relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 text-6xl text-vegas-gold/10">"</div>

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <IconStarFilled key={i} className="w-5 h-5 text-yellow-500" />
                ))}
              </div>

              {/* Testimonial */}
              <p className="text-gray-300 mb-6 italic relative z-10">"{testimonial.text}"</p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="text-4xl">{testimonial.image}</div>
                <div>
                  <div className="text-white font-semibold">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">{testimonial.role}</div>
                  <div className="text-xs text-vegas-gold">{testimonial.company}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 px-6 bg-gradient-to-br from-vegas-gold to-yellow-600 relative overflow-hidden">
      {/* Animated Background */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0 opacity-10"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_transparent_40%,_black_100%)]" />
      </motion.div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl font-bold text-black mb-6">
            Ready to Elevate Your Parking Experience?
          </h2>
          <p className="text-xl text-black/80 mb-10">
            Get a custom quote in under 2 minutes. No commitments, just exceptional service.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-black text-vegas-gold rounded-xl font-bold text-lg hover:bg-gray-900 transition-all shadow-2xl"
            >
              Get Your Free Quote
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm border-2 border-black/20 text-black rounded-xl font-bold text-lg hover:bg-white/20 transition-all"
            >
              Schedule a Call
            </motion.button>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 items-center">
            <div className="flex items-center gap-2 text-black/70">
              <IconShieldCheck className="w-6 h-6" />
              <span className="font-semibold">Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 text-black/70">
              <IconCheck className="w-6 h-6" />
              <span className="font-semibold">Licensed & Bonded</span>
            </div>
            <div className="flex items-center gap-2 text-black/70">
              <IconTrophy className="w-6 h-6" />
              <span className="font-semibold">Award Winning</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Modern Footer
const Footer = () => {
  return (
    <footer className="bg-black border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-bold text-vegas-gold mb-4">OpenDoorValet</h3>
            <p className="text-gray-400 mb-4">
              Excellence in hospitality-driven valet services since 2009.
            </p>
            <div className="flex gap-3">
              <a href="#" className="p-2 bg-gray-900 rounded-lg text-vegas-gold hover:bg-vegas-gold hover:text-black transition-all">
                <IconBrandFacebook className="w-5 h-5" />
              </a>
              <a href="#" className="p-2 bg-gray-900 rounded-lg text-vegas-gold hover:bg-vegas-gold hover:text-black transition-all">
                <IconBrandInstagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#" className="hover:text-vegas-gold transition-colors">Commercial Valet</a></li>
              <li><a href="#" className="hover:text-vegas-gold transition-colors">Event Services</a></li>
              <li><a href="#" className="hover:text-vegas-gold transition-colors">Shuttle Services</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#" className="hover:text-vegas-gold transition-colors">About Us</a></li>
              <li><a href="#" className="hover:text-vegas-gold transition-colors">Careers</a></li>
              <li><a href="#" className="hover:text-vegas-gold transition-colors">Blog</a></li>
              <li><a href="#" className="hover:text-vegas-gold transition-colors">GSEAT Platform</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <IconMapPin className="w-5 h-5 text-vegas-gold flex-shrink-0" />
                <span>NJ, PA & Beyond</span>
              </li>
              <li className="flex items-center gap-2">
                <IconPhone className="w-5 h-5 text-vegas-gold flex-shrink-0" />
                <a href="tel:" className="hover:text-vegas-gold transition-colors">Get Quote</a>
              </li>
              <li className="flex items-center gap-2">
                <IconMail className="w-5 h-5 text-vegas-gold flex-shrink-0" />
                <a href="mailto:" className="hover:text-vegas-gold transition-colors">Contact Us</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
          <div>© 2009-2025 Open Door Valet. All rights reserved.</div>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="#" className="hover:text-vegas-gold transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-vegas-gold transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
export default function LandingPage() {
  return (
    <div className="bg-black overflow-x-hidden">
      <HeroSection />
      <ServicesSection />
      <PlatformSection />
      <TestimonialsSection />
      <CTASection />
      <Footer />
    </div>
  );
}
